
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import NoteContent from './editor/NoteContent';

const SharedNoteView = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [note, setNote] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    if (shareId) {
      fetchNote();
    } else {
      handleInvalidShare("This shared note link is invalid.");
    }
  }, [shareId]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setIsAuthenticated(!!user);
  };

  const handleInvalidShare = (message: string) => {
    toast({
      title: "Invalid share link",
      description: message,
      variant: "destructive",
    });
    navigate('/');
  };

  const fetchNote = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching note with shareId:', shareId);
      
      // First verify access using the security function
      const { data: hasAccess, error: accessError } = await supabase
        .rpc('verify_note_share_access', { p_share_id: shareId });

      if (accessError) {
        console.error('Error verifying access:', accessError);
        handleInvalidShare("Failed to verify access to the shared note.");
        return;
      }

      if (!hasAccess) {
        handleInvalidShare("You don't have permission to access this shared note.");
        return;
      }
      
      // Now fetch the note data using the secure RPC function
      const { data, error } = await supabase
        .rpc('get_shared_note', { p_share_id: shareId });

      if (error) {
        console.error('Error fetching note:', error);
        handleInvalidShare("Failed to load the shared note.");
        return;
      }
      
      if (!data) {
        handleInvalidShare("This shared note doesn't exist or has been deleted.");
        return;
      }

      // Additional security check: verify the note sharing flags
      if (data.is_public && !data.has_public_share) {
        handleInvalidShare("This note is not configured for public sharing.");
        return;
      }
      
      if (!data.is_public && !data.has_private_share) {
        handleInvalidShare("This note is not configured for private sharing.");
        return;
      }

      console.log('Found note:', data);
      setNote(data);
      setTitle(data.title);
      setContent(data.content);
    } catch (error) {
      console.error('Error fetching shared note:', error);
      handleInvalidShare("Failed to load the shared note.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { returnUrl: `/share/${shareId}` } });
      return;
    }

    try {
      const { error } = await supabase
        .from('notes')
        .update({ title, content })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Changes saved",
        description: "Your changes have been saved successfully.",
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: "Error saving changes",
        description: "Failed to save your changes.",
        variant: "destructive",
      });
    }
  };

  const handleCreateOwnNote = () => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { returnUrl: '/' } });
      return;
    }
    navigate('/', { state: { note: { title, content, color: note.color, coverImage: note.cover_image } } });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center">
        <div className="text-lg">Loading shared note...</div>
      </div>
    );
  }

  if (!note) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="p-6" style={{ backgroundColor: note.color }}>
        {note.cover_image && (
          <div className="w-full h-48 mb-4">
            <img
              src={note.cover_image}
              alt={note.title}
              className="w-full h-full object-cover rounded-t"
            />
          </div>
        )}
        
        <NoteContent
          title={title}
          content={content}
          onTitleChange={note.is_public && note.collaborative_editing ? setTitle : undefined}
          onContentChange={note.is_public && note.collaborative_editing ? setContent : undefined}
          backgroundColor={note.color}
          isWysiwygMode={true}
        />

        <div className="mt-4 space-y-2">
          {note.is_public && note.collaborative_editing ? (
            isAuthenticated ? (
              <Button onClick={handleSave}>Save Changes</Button>
            ) : (
              <Button onClick={() => navigate('/auth', { state: { returnUrl: `/share/${shareId}` } })}>
                Sign in to Edit
              </Button>
            )
          ) : (
            <Button onClick={handleCreateOwnNote}>
              {isAuthenticated ? "Create Your Own Note" : "Sign in to Create Your Own Note"}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SharedNoteView;
